import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import clsx from 'clsx';
import { Icon } from '../../../../layout/Icon.jsx';

function defaultsFrom(pkg) {
  return {
    name: pkg?.name || '',
    isOpenTime: !!pkg?.isOpenTime,
    durationMinutes: pkg?.durationMinutes ?? 60,
    price: pkg?.price ?? 10000,
    extraHourPrice: pkg?.extraHourPrice ?? 0,
    description: pkg?.description || '',
    isActive: pkg?.isActive !== false
  };
}

export function PackageForm({ open, pkg, onClose, onSubmit, submitting }) {
  const isEdit = !!pkg;
  // «وقت مفتوح (بدون مدة محددة)» لم يعد خياراً عند إنشاء باقة جديدة.
  // يبقى ظاهراً — للعرض وللتراجع عنه — في الباقات القديمة المحفوظة به وحدها،
  // فلا تتغيّر بياناتها ولا طريقة حساب وقتها، ولا يستطيع أحد إنشاء باقة
  // جديدة بوقت مفتوح من هذه النافذة.
  const legacyOpenTime = !!pkg?.isOpenTime;
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm({ defaultValues: defaultsFrom(pkg) });

  useEffect(() => {
    if (open) reset(defaultsFrom(pkg));
  }, [open, pkg, reset]);

  // الباقة الجديدة لها مدة دائماً — لا حالة «وقت مفتوح» تُعطّل حقل المدة.
  const isOpenTime = legacyOpenTime && watch('isOpenTime');

  if (!open) return null;

  const submit = (values) => {
    if (!values.name || !values.name.trim()) return;
    const openTime = legacyOpenTime && !!values.isOpenTime;
    // المدة مطلوبة لكل باقة جديدة — الفحص المرتبط بالوقت المفتوح لم يعد
    // ينطبق إلا على باقة قديمة ما زالت محفوظة به.
    if (!openTime && !(Number(values.durationMinutes) > 0)) return;
    if (!(Number(values.price) >= 0)) return;
    const body = {
      name: values.name.trim(),
      durationMinutes: openTime ? null : Number(values.durationMinutes) || 0,
      price: Number(values.price) || 0,
      extraHourPrice: Number(values.extraHourPrice) || 0,
      description: values.description || '',
      isActive: !!values.isActive
    };
    // لا نرسل الحقل إطلاقاً إلا للباقة القديمة: الإنشاء يأخذ الافتراضي (false)
    // من الخادم، وتعديل باقة عادية لا يمسّ قيمتها المحفوظة.
    if (legacyOpenTime) body.isOpenTime = openTime;
    onSubmit?.(body);
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <form
        className="card w-[560px] max-w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">
            {isEdit ? 'تعديل باقة' : 'باقة جديدة'}
          </h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="card-b space-y-3">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الاسم *</label>
            <input
              className={clsx('input', errors.name && 'border-red')}
              placeholder="مثلاً: باقة ساعتين"
              {...register('name', { required: true })}
            />
          </div>

          {legacyOpenTime ? (
            <div className="rounded-[10px] p-2.5 space-y-1.5" style={{ background: '#FFF7ED' }}>
              <label className="flex items-center gap-2 cursor-pointer text-[13px] text-ink-2">
                <input type="checkbox" className="accent-orange" {...register('isOpenTime')} />
                وقت مفتوح (بدون مدة محددة)
              </label>
              <div className="text-[11px] text-ink-3">
                باقة قديمة محفوظة بوقت مفتوح. الخيار لم يعد متاحاً للباقات الجديدة —
                يظهر هنا فقط حتى لا تتغيّر هذه الباقة من تلقاء نفسها، ويمكنك إلغاؤه
                وتحديد مدة إن أردت.
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">
                المدة (دقيقة) {isOpenTime ? null : <span className="text-red">*</span>}
              </label>
              <input
                className="input"
                type="number"
                min="0"
                disabled={isOpenTime}
                {...register('durationMinutes', { valueAsNumber: true })}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">السعر (د.ع) *</label>
              <input
                className="input"
                type="number"
                min="0"
                {...register('price', { valueAsNumber: true, required: true })}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-ink-2 mb-1">سعر الساعة الإضافية (د.ع)</label>
              <input
                className="input"
                type="number"
                min="0"
                {...register('extraHourPrice', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الوصف</label>
            <textarea
              className="input"
              rows={2}
              {...register('description')}
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-[13px] text-ink-2">
            <input type="checkbox" className="accent-orange" {...register('isActive')} />
            فعّالة
          </label>
        </div>

        <div className="card-h border-b-0 border-t flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            إلغاء
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'جاري الحفظ...' : isEdit ? 'حفظ' : 'إنشاء'}
          </button>
        </div>
      </form>
    </div>
  );
}
